// apps/web/src/components/analisis/MapaAnalisis.tsx - ACTUALIZADO PARA LÍNEAS
import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { GeometriaKML, CentroMapa } from '../../services/analisisService';
import { useGoogleMapsLoader } from '../../services/googleMaps/googleMapsLoader';

// ===============================
// INTERFACES
// ===============================

interface GeometriaMapaAnalisis {
  id: string;
  geometria: GeometriaKML;
  color: string;
  googlePolygon?: google.maps.Polygon;
  googlePolyline?: google.maps.Polyline; // ✅ NUEVO: para líneas
  tipo: 'POLYGON' | 'LINESTRING'; // ✅ NUEVO
}

interface MapaAnalisisProps {
  geometrias: GeometriaKML[];
  centroMapa: CentroMapa;
}

// ===============================
// CONFIGURACIÓN
// ===============================

const COLORES_POLIGONOS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
  '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
  '#10AC84', '#EE5A24', '#0984E3', '#6C5CE7', '#A29BFE'
];

// ✅ NUEVO: Colores para líneas (más vibrantes)
const COLORES_LINEAS = [
  '#FF4757', '#2ED573', '#1E90FF', '#FF6B35', '#A55EEA',
  '#26C6DA', '#FFA726', '#EC407A', '#42A5F5', '#AB47BC'
];

// ===============================
// COMPONENTE
// ===============================

export const MapaAnalisis: React.FC<MapaAnalisisProps> = ({
  geometrias,
  centroMapa
}) => {
  const { t } = useTranslation();
  // Hook para cargar Google Maps
  const { isLoading: googleMapsLoading, isReady: googleMapsReady, error: googleMapsError } = useGoogleMapsLoader();
  
  // Estados
  const [geometriasMapa, setGeometriasMapa] = useState<GeometriaMapaAnalisis[]>([]);
  const [procesando, setProcesando] = useState(false);
  const [mapaIniciado, setMapaIniciado] = useState(false);
  
  // Referencias
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);

  // ===============================
  // LIMPIAR GEOMETRÍAS ANTERIORES
  // ===============================

  const limpiarGeometriasAnteriores = () => {
    // Remover geometrías anteriores del mapa
    geometriasMapa.forEach(geometriaMapa => {
      if (geometriaMapa.googlePolygon) {
        geometriaMapa.googlePolygon.setMap(null);
      }
      if (geometriaMapa.googlePolyline) {
        geometriaMapa.googlePolyline.setMap(null);
      }
    });
    
    // Limpiar estado
    setGeometriasMapa([]);
  };

  // ===============================
  // INICIALIZAR GOOGLE MAPS
  // ===============================

  const inicializarMapa = async () => {
    if (!mapRef.current || mapaIniciado || !googleMapsReady) {
      return;
    }

    try {      
      // Verificar que google esté disponible
      if (typeof google === 'undefined' || !google.maps) {
        throw new Error('Google Maps no está disponible');
      }
      
      // Usar la misma API que MapaPreview que funciona
      const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
      
      // Crear mapa igual que en MapaPreview
      const mapa = new Map(mapRef.current, {
        center: centroMapa ? {
          lat: centroMapa.latitud,
          lng: centroMapa.longitud
        } : { lat: -33.4489, lng: -70.6693 },
        zoom: centroMapa ? 12 : 10,
        mapTypeId: google.maps.MapTypeId.SATELLITE,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.TOP_CENTER,
          mapTypeIds: [
            google.maps.MapTypeId.ROADMAP,
            google.maps.MapTypeId.SATELLITE,
            google.maps.MapTypeId.HYBRID,
            google.maps.MapTypeId.TERRAIN
          ]
        }
      });

      googleMapRef.current = mapa;
      setMapaIniciado(true);

    } catch (error) {
      console.error('Error inicializando mapa:', error);
      toast.error(`Error al inicializar el mapa: ${(error as any)?.message || error}`);
    }
  };

  // ===============================
  // PROCESAR GEOMETRÍAS
  // ===============================

  const procesarGeometrias = () => {
    if (!geometrias || geometrias.length === 0) {
      limpiarGeometriasAnteriores();
      return;
    }
    // Limpiar geometrías anteriores antes de procesar nuevas
    limpiarGeometriasAnteriores();

    setProcesando(true);

    const geometriasMapaTemp: GeometriaMapaAnalisis[] = [];

    geometrias.forEach((geom, index) => {
      try {
        const uniqueKey = `geom-${geom.id}-${geom.reporte.id}-${geom.orden}-${index}`;
        const esLinea = geom.tipo_geometria === 'LINESTRING' || (geom.area_hectareas === 0 && geom.longitud_metros && geom.longitud_metros > 0);

        // Usar colores diferentes para líneas vs polígonos
        const color = esLinea
          ? COLORES_LINEAS[index % COLORES_LINEAS.length]
          : COLORES_POLIGONOS[index % COLORES_POLIGONOS.length];

        geometriasMapaTemp.push({
          id: uniqueKey,
          geometria: geom,
          color,
          tipo: esLinea ? 'LINESTRING' : 'POLYGON'
        });

      } catch (error) {
        console.error(`Error procesando geometría ${geom.id}:`, error);
      }
    });

    setGeometriasMapa(geometriasMapaTemp);
    setProcesando(false);
  };

  // ===============================
  // PARSEAR WKT - SOPORTE LÍNEAS Y POLÍGONOS
  // ===============================

  const parsearWKT = (wkt: string): google.maps.LatLngLiteral[] => {
    try {
      if (!wkt || typeof wkt !== 'string') {
        return [];
      }

      // PARSEAR POLYGON
      const polygonMatch = wkt.match(/POLYGON\s*\(\s*\((.*?)\)\s*\)/i);
      if (polygonMatch) {
        const coordsStr = polygonMatch[1];
        const coordPairs = coordsStr.split(',').map(pair => pair.trim()).filter(pair => pair.length > 0);

        const coordinates = coordPairs.map(pair => {
          const [lng, lat] = pair.split(/\s+/).map(Number);

          if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            return null;
          }

          return { lat, lng };
        }).filter(coord => coord !== null) as google.maps.LatLngLiteral[];

        return coordinates;
      }
      
      // PARSEAR LINESTRING
      const lineMatch = wkt.match(/LINESTRING\s*\(([^)]+)\)/i);
      if (lineMatch) {
        const coordsStr = lineMatch[1];
        const coordPairs = coordsStr.split(',').map(pair => pair.trim()).filter(pair => pair.length > 0);

        const coordinates = coordPairs.map(pair => {
          const [lng, lat] = pair.split(/\s+/).map(Number);

          if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            return null;
          }

          return { lat, lng };
        }).filter(coord => coord !== null) as google.maps.LatLngLiteral[];

        return coordinates;
      }

      return [];

    } catch (error) {
      return [];
    }
  };

  // ===============================
  // DIBUJAR GEOMETRÍAS EN MAPA
  // ===============================

  const dibujarGeometriasEnMapa = () => {
    if (!googleMapRef.current || geometriasMapa.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    let geometriasValidas = 0;

    geometriasMapa.forEach((geometriaMapa) => {
      const { geometria, color, tipo } = geometriaMapa;

      try {
        const coordinates = parsearWKT(geometria.geometria_wkt);

        if (coordinates.length === 0) {
          return;
        }

        if (tipo === 'POLYGON') {
          // CREAR POLÍGONO
          const googlePolygon = new google.maps.Polygon({
            paths: coordinates,
            strokeColor: color,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: color,
            fillOpacity: 0.35,
            clickable: true
          });

          // Agregar al mapa
          googlePolygon.setMap(googleMapRef.current);

          // Event listener para click
          googlePolygon.addListener('click', () => {
            toast(`📍 ${geometria.nombre}\n🏞️ Área: ${geometria.area_hectareas.toFixed(2)} ha\n🚜 Máquina: ${geometria.reporte.maquina_codigo}`, {
              duration: 4000,
              icon: '🗺️'
            });
          });

          // Guardar referencia al polígono
          geometriaMapa.googlePolygon = googlePolygon;
          
        } else if (tipo === 'LINESTRING') {
          // CREAR LÍNEA
          const googlePolyline = new google.maps.Polyline({
            path: coordinates,
            strokeColor: color,
            strokeOpacity: 0.9,
            strokeWeight: 4,
            clickable: true
          });

          // Agregar al mapa
          googlePolyline.setMap(googleMapRef.current);

          // Event listener para click
          googlePolyline.addListener('click', () => {
            const longitudKm = (geometria.longitud_metros || 0) / 1000;
            toast(`📍 ${geometria.nombre}\n📏 Longitud: ${longitudKm.toFixed(2)} km\n🚜 Máquina: ${geometria.reporte.maquina_codigo}`, {
              duration: 4000,
              icon: '📏'
            });
          });

          // Guardar referencia a la línea
          geometriaMapa.googlePolyline = googlePolyline;
        }

        // Extender bounds
        coordinates.forEach(coord => {
          bounds.extend(coord);
        });

        geometriasValidas++;

      } catch (error) {
        console.error(`Error dibujando ${tipo.toLowerCase()} ${geometria.nombre}:`, error);
      }
    });

    // Auto-zoom a todas las geometrías
    if (geometriasValidas > 0 && !bounds.isEmpty()) {
      googleMapRef.current.fitBounds(bounds);

      // Si hay muchas geometrías, no hacer zoom tan extremo
      google.maps.event.addListenerOnce(googleMapRef.current, 'bounds_changed', () => {
        if (googleMapRef.current && googleMapRef.current.getZoom()! > 18) {
          googleMapRef.current.setZoom(18);
        }
      });
    }
  };

  // ===============================
  // EFECTOS
  // ===============================

  // Efecto para inicializar mapa cuando Google Maps está listo
  useEffect(() => {
    if (googleMapsReady && mapRef.current && !mapaIniciado) {
      inicializarMapa();
    }
  }, [googleMapsReady, mapaIniciado]);

  // Efecto para procesar geometrías cuando cambian
  useEffect(() => {
    procesarGeometrias();
  }, [geometrias]);

  // Efecto para dibujar geometrías cuando se cargan
  useEffect(() => {
    if (mapaIniciado && geometriasMapa.length > 0) {
      dibujarGeometriasEnMapa();
    }
  }, [mapaIniciado, geometriasMapa]);

  // ===============================
  // RENDER
  // ===============================

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div className="bg-gray-50 px-6 py-3 border-b">
        <h3 className="text-lg font-medium text-gray-900">🗺️ {t('kml.map_preview')}</h3>
        <p className="text-sm text-gray-500 mt-1">
          {procesando ? (
            <span className="text-blue-600">{t('common.processing')}...</span>
          ) : googleMapsLoading ? (
            <span className="text-blue-600">{t('common.loading')}</span>
          ) : googleMapsError ? (
            <span className="text-red-600">{t('errors.load_error')}: {googleMapsError}</span>
          ) : (
            <>
              {geometriasMapa.length} geometrías dibujadas ({geometriasMapa.filter(g => g.tipo === 'POLYGON').length} polígonos, {geometriasMapa.filter(g => g.tipo === 'LINESTRING').length} líneas) - Click para más detalles
              {geometriasMapa.length === 0 && geometrias && geometrias.length > 0 && (
                <span className="text-amber-600"> {t('errors.unknown_error')}</span>
              )}
              {geometriasMapa.length > 0 && (
                <span className="text-green-600"> {t('common.success')}</span>
              )}
            </>
          )}
        </p>
      </div>
      
      <div style={{ height: '500px' }}>
        <div 
          ref={mapRef}
          className="w-full h-full"
        />
      </div>
      
      {/* Debug info */}
      {import.meta.env.VITE_DEBUG === 'true' && (
        <div className="bg-gray-100 p-2 text-xs">
          <strong>Debug:</strong> Geometrías: {geometrias?.length || 0}, Procesadas: {geometriasMapa.length}, 
          Mapa iniciado: {mapaIniciado ? 'Sí' : 'No'}, Google Maps: {googleMapsReady ? 'Listo' : 'Cargando'}, 
          Procesando: {procesando ? 'Sí' : 'No'}
        </div>
      )}
    </div>
  );
};

export default MapaAnalisis;